import { format, parseISO } from 'date-fns';
import { QuoteFlow } from '../resources/base/models/enums';
import { isProductForNewCar, isProductForOldCar } from '../resources/base/models/financialProduct';
import { VehicleType } from '../resources/base/models/vehicleTypes';
import { copyQuote, getQuote } from '../services/api/miles/miles-services';
import { assertDefined, assertDefinedVoid, waitForMiles } from '../services/common/miscUtils';
import { store } from '../services/redux';
import { setIsCarUsed, setSelectedVehicleType } from '../services/redux/features/custom.slice';
import { setCustomerInitialState } from '../services/redux/features/customer.slice';
import { loadingActions } from '../services/redux/features/loading.slice';
import { setQuoteData, setQuoteFlow } from '../services/redux/features/quote.slice';
import { getInitialCustomerStateFromQuote } from '../services/redux/reduxUtils';
import { NavigateType } from '../services/routing/navigate';

export const roundNumber = (num: number) => {
  const rounded = Math.round((num + Number.EPSILON) * 100) / 100;
  return rounded.toFixed(2);
};

export const totalCost = (a: number = 0, b: number = 0, c: number = 0, d: number = 0, e: number = 0) => {
  return roundNumber(a + b + c + d + e);
};

/**
 * Returns a date that is a given number of months before the current date,
 * preserving the current day of the month.
 *
 * @param offset - The number of months to subtract from the current date.
 * @returns A new Date object offset by the specified number of months.
 */
export const getDateWithMonthOffset = (offset: number): Date => {
  const currentDate = new Date();
  return new Date(currentDate.getFullYear(), currentDate.getMonth() - offset, currentDate.getDate());
};

export const resolveKeys = (key: string, texts: any, defaultValue?: string) => {
  try {
    return key
      .split(/[.[]/)
      .map((k) => k.replace(/]/, ''))
      .reduce((p, c) => p[c], texts);
  } catch (err) {
    return defaultValue;
  }
};

export const scrollToTop = () => {
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);
};

export const getBase64 = (file): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      if (typeof fileReader.result === 'string') {
        resolve(fileReader.result.split(',')[1]);
      } else {
        reject(new Error("File is broken, can't get base64"));
      }
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

/**
 * Formats a date string from "YYYY-MM-DD" to "DD/MM/YYYY".
 * @param dateString - The input date string in "YYYY-MM-DD" format.
 * @returns The formatted date string in "DD/MM/YYYY" format.
 */
export const formatDate = (dateString: string): string => {
  const date = parseISO(dateString);
  return format(date, 'dd/MM/yyyy');
};

export const getVehicleType = (quote: QuoteSchema.Schemas.QuoteResponse): VehicleType => {
  const productName = assertDefined(
    quote?.financialProduct?.offers?.[0]?.productName,
    'Quote productName is not defined'
  );

  let vehicleType: VehicleType | undefined;

  if (isProductForNewCar(productName)) {
    if (quote.vehicleData?.vin) {
      vehicleType = VehicleType.STOCK;
    } else {
      vehicleType = VehicleType.NEW;
    }
  } else if (isProductForOldCar(productName)) {
    vehicleType = VehicleType.USED;
  }

  assertDefinedVoid(vehicleType, 'Vehicle type could not be inferred.');

  return vehicleType;
};

const getCarUsedByMilesVehicleType = (milesVehicleType?: QuoteSchema.Schemas.MilesVehicleType): boolean | null => {
  switch (milesVehicleType) {
    case 'New':
    case 'Stock':
      return false;
    case 'Used':
      return true;
    case 'Demo':
    default:
      return null;
  }
};

export const isCarUsedFromQuote = (quote: QuoteSchema.Schemas.QuoteResponse): boolean => {
  const _isCarUsed = getCarUsedByMilesVehicleType(quote.vehicleData?.milesVehicleType);

  if (_isCarUsed === null) {
    const _vehicleType = getVehicleType(quote);
    return _vehicleType === VehicleType.USED;
  }

  return _isCarUsed;
};

export const handleCopyQuote = async (quoteId: string, navigate: NavigateType, redirectTo: number = 1) => {
  try {
    store.dispatch(loadingActions.setLoading(true));
    const copiedQuoteResponse = await copyQuote(quoteId);
    const copiedQuoteId = copiedQuoteResponse?.financialProduct?.offers?.[0]?.offerId;

    if (!copiedQuoteId) {
      throw new Error('Missing quote id!');
    }

    await waitForMiles();
    const quoteData = await getQuote(copiedQuoteId);
    store.dispatch(setIsCarUsed(isCarUsedFromQuote(quoteData)));
    const customerInitialState = await getInitialCustomerStateFromQuote(quoteData);
    store.dispatch(setCustomerInitialState(customerInitialState));
    store.dispatch(setSelectedVehicleType(getVehicleType(quoteData)));
    store.dispatch(setQuoteData(quoteData));
    store.dispatch(setQuoteFlow(QuoteFlow.COPY));
    store.dispatch(loadingActions.setLoading(false));
    navigate.form(redirectTo);
  } catch {
    store.dispatch(loadingActions.setLoading(false));
    navigate.error();
  }
};

export const preventCopyQuoteStatuses = ['23'];

export const additionalProductsTranslationsMap = {
  'GAP damage insurance Private': 'Perte financière',
  'GAP damage insurance professional': 'Perte financière',
  'Personal Credit insurance Private': 'Assurance à la personne',
  'Personal Credit insurance professional': 'Assurance à la personne',
  'RMT Private': 'Entretien du Véhicule',
  'Repair and Maintenance Professional': 'Maintenance',
  'Relief Vehicle private': 'Véhicule de remplacement',
  'Relief Vehicle professional': 'Véhicule de remplacement',
  'Summer tires': 'Pneumatiques été',
  'Winter tires': 'Pneumatiques hiver',
  'All season tires': 'Pneumatiques toutes saisons',
  'Warranty Extension': 'Extension de garantie',
};
